import React, { useState, useEffect } from 'react';
import { Send, FileText, Plus, Trash2, User, Car, ShoppingCart, DollarSign } from 'lucide-react';
import { solicitudesService, clientesService, serviciosService, subserviciosService, categoriasSolicitudService } from '../../../../services/api';
import { useAuth } from '../../../../hooks/useAuth';
import AlertModal from './AlertModal';

const DEFAULTS = {
  servicio: 'Multiservicios',
  subservicio: 'Mantenimiento',
  categoria: 'Externo / Multi-Servicios'
};

const OrdenesTrabajoTab = ({ vehiculos }) => {
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [defaultIds, setDefaultIds] = useState({
    servicio: null,
    subservicio: null,
    categoria: null
  });
  const [alert, setAlert] = useState({ open: false, title: '', message: '', type: 'info' });
  
  // Form State
  const [formData, setFormData] = useState({
    cliente_id: '',
    vehiculo_id: '',
    descripcion: '',
  });

  const [repuestos, setRepuestos] = useState([]);
  const [nuevoRepuesto, setNuevoRepuesto] = useState({ nombre: '', cantidad: 1, costo: 0 });

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [clientesRes, serviciosRes, subserviciosRes, categoriasRes] = await Promise.all([
          clientesService.getAll({}),
          serviciosService.getAll({}),
          subserviciosService.getAll({}),
          categoriasSolicitudService.getAll({})
        ]);

        setClientes(clientesRes.data || []);

        const servicio = (serviciosRes.data || []).find(s => s.nombre === DEFAULTS.servicio);
        const subservicio = (subserviciosRes.data || []).find(s => s.nombre === DEFAULTS.subservicio);
        const categoria = (categoriasRes.data || []).find(c => c.nombre === DEFAULTS.categoria);

        setDefaultIds({
          servicio: servicio?.id || null,
          subservicio: subservicio?.id || null,
          categoria: categoria?.id || null
        });

        console.log('Defaults loaded:', {
          servicio: servicio ? `${servicio.nombre} (id: ${servicio.id})` : 'NOT FOUND',
          subservicio: subservicio ? `${subservicio.nombre} (id: ${subservicio.id})` : 'NOT FOUND',
          categoria: categoria ? `${categoria.nombre} (id: ${categoria.id})` : 'NOT FOUND'
        });
      } catch (error) {
        console.error('Error cargando datos iniciales:', error);
        setClientes([]);
      }
    };
    fetchInitialData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleAddRepuesto = () => {
    if (!nuevoRepuesto.nombre.trim()) return;
    setRepuestos(prev => [...prev, { ...nuevoRepuesto, id: Date.now(), costo: Number(nuevoRepuesto.costo) || 0 }]);
    setNuevoRepuesto({ nombre: '', cantidad: 1, costo: 0 });
  };

  const handleRemoveRepuesto = (id) => {
    setRepuestos(prev => prev.filter(r => r.id !== id));
  };

  const totalRepuestos = repuestos.reduce((sum, r) => sum + (Number(r.costo) || 0) * (Number(r.cantidad) || 0), 0);

  const showAlert = (title, message, type = 'info') => {
    setAlert({ open: true, title, message, type });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.cliente_id || !formData.vehiculo_id || !formData.descripcion) {
      showAlert('Campos incompletos', 'Por favor complete los campos requeridos (Cliente, Vehículo y Descripción)', 'warning');
      return;
    }

    if (repuestos.length === 0) {
      showAlert('Sin repuestos', 'Debe agregar al menos un repuesto a la orden', 'warning');
      return;
    }

    setLoading(true);
    try {
      const vehiculoSeleccionado = vehiculos.find(v => v.id.toString() === formData.vehiculo_id);
      const placaStr = vehiculoSeleccionado ? vehiculoSeleccionado.placa : formData.vehiculo_id;
      const nombreStr = vehiculoSeleccionado?.nombre || '';

      const payload = {
        id_empresa: parseInt(vehiculoSeleccionado?.empresa_id) || 1,
        id_cliente: parseInt(formData.cliente_id),
        vehiculo_id: parseInt(formData.vehiculo_id),
        id_servicio: defaultIds.servicio || 20,
        id_subservicio: defaultIds.subservicio || null,
        id_categoria_solicitud: defaultIds.categoria || null,
        usuario_asignado: user?.id || null,
        creado_por: user?.id || null,
        nombre_solicitud: `Orden de Trabajo - Vehículo ${placaStr}${nombreStr ? ` (${nombreStr})` : ''}`,
        descripcion: formData.descripcion,
        articulos: repuestos.map(r => ({
          id_articulo: null,
          cantidad: r.cantidad,
          observacion: r.nombre,
          costo: Number(r.costo) || 0,
          precio_venta: Number(r.costo) || 0
        })),
      };

      await solicitudesService.create(payload);

      showAlert('Orden creada', 'Orden de trabajo creada exitosamente. Será redirigida a la lista de órdenes.', 'success');

      setFormData({ cliente_id: '', vehiculo_id: '', descripcion: '' });
      setRepuestos([]);

    } catch (error) {
      console.error('Error creando orden:', error);
      showAlert('Error', error.message || 'Error al crear la orden de trabajo', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseAlert = () => setAlert(prev => ({ ...prev, open: false }));

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-bold text-blue-800 flex items-center gap-2">
          <FileText size={20} />
          Generar Solicitud de Trabajo
        </h2>
        <p className="text-sm text-gray-600">Complete los datos para generar una nueva solicitud que pasará automáticamente a gestión.</p>
      </div>

      <div className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Fila 1: Cliente y Vehículo */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                <User size={16} /> Cliente *
              </label>
              <select
                name="cliente_id"
                value={formData.cliente_id}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                required
              >
                <option value="">Seleccione un cliente...</option>
                {clientes.map(c => (
                  <option key={c.id} value={c.id}>{c.nombre}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                <Car size={16} /> Vehículo *
              </label>
              <select
                name="vehiculo_id"
                value={formData.vehiculo_id}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                required
              >
                <option value="">Seleccione el vehículo...</option>
                {vehiculos && vehiculos.length > 0 ? (
                  vehiculos.map(v => (
                    <option key={v.id} value={v.id}>
                      {[v.nombre, `${v.marca} ${v.modelo}`, v.placa].filter(Boolean).join(' - ')}
                      {v.empresa_nombre ? ` (${v.empresa_nombre})` : ''}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>No hay vehículos registrados</option>
                )}
              </select>
            </div>
          </div>

          {/* Valores por defecto de servicio/subservicio/categoría */}
          <div className="p-4 bg-gradient-to-r from-gray-50 to-blue-50 border border-blue-200 rounded-xl">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Clasificación automática</p>
            <div className="flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                <FileText size={12} />
                Servicio: {DEFAULTS.servicio}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-100 text-purple-800 rounded-full text-xs font-semibold">
                <FileText size={12} />
                SubServicio: {DEFAULTS.subservicio}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-violet-100 text-violet-800 rounded-full text-xs font-semibold">
                <FileText size={12} />
                Categoría: {DEFAULTS.categoria}
              </span>
            </div>
          </div>

          {/* Descripción */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
              <FileText size={16} /> Descripción del Trabajo *
            </label>
            <textarea
              name="descripcion"
              value={formData.descripcion}
              onChange={handleInputChange}
              rows="4"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              placeholder="Detalle el trabajo a realizar..."
              required
            />
          </div>

          {/* Sección de Repuestos */}
          <div className="mt-8 border-t border-gray-200 pt-6">
            <h3 className="text-md font-bold text-gray-800 mb-4 flex items-center gap-2">
              <ShoppingCart size={18} />
              Registro de Repuestos / Necesidades
            </h3>
            
            <div className="flex gap-3 items-end mb-4 bg-gray-50 p-4 rounded-lg border border-gray-200 flex-wrap">
              <div className="flex-1 min-w-[180px]">
                <label className="block text-xs font-medium text-gray-700 mb-1">Nombre del Repuesto</label>
                <input
                  type="text"
                  value={nuevoRepuesto.nombre}
                  onChange={(e) => setNuevoRepuesto({ ...nuevoRepuesto, nombre: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Ej: Filtro de Aceite"
                />
              </div>
              <div className="w-24">
                <label className="block text-xs font-medium text-gray-700 mb-1">Cantidad</label>
                <input
                  type="number"
                  min="1"
                  value={nuevoRepuesto.cantidad}
                  onChange={(e) => setNuevoRepuesto({ ...nuevoRepuesto, cantidad: parseInt(e.target.value) || 1 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="w-28">
                <label className="block text-xs font-medium text-gray-700 mb-1">Costo Unit. ($)</label>
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={nuevoRepuesto.costo}
                    onChange={(e) => setNuevoRepuesto({ ...nuevoRepuesto, costo: parseFloat(e.target.value) || 0 })}
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                  />
                </div>
              </div>
              {nuevoRepuesto.costo > 0 && nuevoRepuesto.cantidad > 0 && (
                <div className="text-xs text-gray-500 pt-6">
                  Subtotal: <span className="font-semibold text-green-700">${(Number(nuevoRepuesto.costo) * Number(nuevoRepuesto.cantidad)).toFixed(2)}</span>
                </div>
              )}
              <button
                type="button"
                onClick={handleAddRepuesto}
                className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 flex items-center gap-2"
              >
                <Plus size={16} /> Agregar
              </button>
            </div>

            {repuestos.length > 0 ? (
              <div className="space-y-2">
                {repuestos.map((repuesto) => {
                  const subtotal = (Number(repuesto.costo) || 0) * (Number(repuesto.cantidad) || 0);
                  return (
                    <div key={repuesto.id} className="flex items-center justify-between bg-white border border-gray-200 px-4 py-3 rounded-lg shadow-sm hover:border-blue-300 transition-colors">
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-gray-800 block truncate">{repuesto.nombre}</span>
                        <div className="flex gap-4 text-xs text-gray-500 mt-0.5">
                          <span>Cant: {repuesto.cantidad}</span>
                          <span>Costo: ${(Number(repuesto.costo) || 0).toFixed(2)} c/u</span>
                          <span className="font-semibold text-green-700">Subtotal: ${subtotal.toFixed(2)}</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveRepuesto(repuesto.id)}
                        className="text-red-500 hover:text-red-700 p-2 rounded-full hover:bg-red-50 transition-colors flex-shrink-0"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  );
                })}
                <div className="flex justify-end items-center gap-2 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <DollarSign size={16} className="text-blue-600" />
                  <span className="text-sm font-semibold text-gray-700">Total Repuestos:</span>
                  <span className="text-lg font-bold text-blue-900">${totalRepuestos.toFixed(2)}</span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic text-center py-4">No se han agregado repuestos aún.</p>
            )}
          </div>

          <div className="flex justify-end pt-4">
            <button
              type="submit"
              disabled={loading || vehiculos.length === 0}
              title={vehiculos.length === 0 ? "Debe registrar un vehículo primero" : ""}
              className="px-6 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 flex items-center gap-2 shadow-lg shadow-blue-600/30 transition-all active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Send size={18} />
              )}
              Generar Solicitud
            </button>
          </div>

        </form>
      </div>

      <AlertModal
        open={alert.open}
        onClose={handleCloseAlert}
        title={alert.title}
        message={alert.message}
        type={alert.type}
      />
    </div>
  );
};

export default OrdenesTrabajoTab;
